import { useMemo, useRef } from 'react';

export interface StableFieldIds {
    getId(serializedPath: string): string;
    handleRename(oldSerializedPath: string, newSerializedPath: string): void;
    handleRemove(serializedPath: string): void;
}

// Serialized paths are JSON arrays, so every descendant of a path shares its
// serialized form minus the closing bracket, followed by a comma.
function toDescendantPrefix(serializedPath: string): string {
    return `${serializedPath.slice(0, -1)},`;
}

// Mints a stable identity per schema path so UI state (open editor, expanded
// groups, manual-key overrides) survives property renames. Test ids stay
// derived from human key paths via getPathId; these ids are UI-state only.
export default function useStableFieldIds(): StableFieldIds {
    const idsRef = useRef(new Map<string, string>());
    const counterRef = useRef(0);

    return useMemo(
        () => ({
            getId(serializedPath: string): string {
                const existing = idsRef.current.get(serializedPath);
                if (existing) {
                    return existing;
                }
                counterRef.current += 1;
                const id = `field-${counterRef.current}`;
                idsRef.current.set(serializedPath, id);
                return id;
            },
            handleRename(oldSerializedPath: string, newSerializedPath: string): void {
                if (oldSerializedPath === newSerializedPath) {
                    return;
                }
                const oldPrefix = toDescendantPrefix(oldSerializedPath);
                const newPrefix = toDescendantPrefix(newSerializedPath);
                const next = new Map<string, string>();
                idsRef.current.forEach((id, path) => {
                    if (path === oldSerializedPath) {
                        next.set(newSerializedPath, id);
                    } else if (path.startsWith(oldPrefix)) {
                        next.set(`${newPrefix}${path.slice(oldPrefix.length)}`, id);
                    } else {
                        next.set(path, id);
                    }
                });
                idsRef.current = next;
            },
            handleRemove(serializedPath: string): void {
                const prefix = toDescendantPrefix(serializedPath);
                const next = new Map<string, string>();
                idsRef.current.forEach((id, path) => {
                    if (path !== serializedPath && !path.startsWith(prefix)) {
                        next.set(path, id);
                    }
                });
                idsRef.current = next;
            },
        }),
        [],
    );
}
